import { useEffect, useState } from 'react';
import {
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useDispatch, useSelector } from 'react-redux';
import { createRewardBody } from '../../../services/api/apiPayloads';
import {
    closeRewardEditor,
    createReward,
    deleteReward,
    updateEditorField,
    updateReward,
} from '../../../store/actions/rewardActions';

const promotionTypes = [
    'MGM',
    'MGM_MILESTONE',
    'PICK_A_PRIZE',
    'LUCKY_PROMOTION',
    'REWARD',
];

const Reward = () => {
    const dispatch = useDispatch();
    const { rewardEditor, rewardItem } = useSelector((state) => state?.reward);
    const { prizeItems } = useSelector((state) => state?.prize);
    const {hasReadOnly} = useSelector((state) => state?.auth?.user)
    const [isDisabled, setIsDisabled] = useState(false);

    const handleSave = (status) => {
        if (status === 'CANCEL') {
            dispatch(closeRewardEditor());
            return;
        }
        if (status === 'DELETE') {
            dispatch(deleteReward(rewardEditor.id));
            return;
        }
        const rewardBody = createRewardBody(
            rewardItem.promotionType,
            rewardItem.name,
            rewardItem.description,
            rewardItem.deductedPoint,
            rewardItem.winRate,
            rewardItem.winLimit,
            rewardItem.winLimitPerUser,
            rewardItem.interval,
            rewardItem.startDate,
            rewardItem.endDate,
            rewardItem.loyaltyPrizeItems,
            status
        );

        if (rewardEditor.id) {
            dispatch(updateReward(rewardEditor.id, rewardBody));
        } else {
            dispatch(createReward(rewardBody));
        }
    };
    const handleInputUpdate = (name, value) => {
        if (name === 'endDate') {
            if (value < rewardItem.startDate) value = rewardItem.startDate;
        }
        dispatch(updateEditorField(name, value));
    };
    useEffect(() => {
        setIsDisabled(
            hasReadOnly || rewardItem.status === 'ACTIVE' || rewardItem.status === 'EXPIRED'
        );
    }, []);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                mt: 2,
                maxWidth: 800,
            }}
        >
            <FormControl fullWidth>
                <InputLabel
                    sx={(theme) => ({
                        backgroundColor: theme.palette.background.main,
                    })}
                    variant="outlined"
                    id="promotion-type-label"
                >
                    Promotion Type
                </InputLabel>
                <Select
                    value={rewardItem.promotionType}
                    onChange={(e) =>
                        handleInputUpdate('promotionType', e.target.value)
                    }
                    labelId="promotion-type-label"
                    disabled={isDisabled}
                >
                    {promotionTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                            {type === 'MGM'
                                ? 'MGM DEFAULT'
                                : type.replaceAll('_', ' ')}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <TextField
                fullWidth
                label="Name"
                value={rewardItem.name || ''}
                onChange={(e) => handleInputUpdate('name', e.target.value)}
                disabled={isDisabled}
            />
            <TextField
                fullWidth
                label="Description"
                value={rewardItem.description || ''}
                onChange={(e) =>
                    handleInputUpdate('description', e.target.value)
                }
                disabled={isDisabled}
            />
            {rewardItem.promotionType === 'REWARD' && (
                <TextField
                    fullWidth
                    label="Deducted Point"
                    type="number"
                    value={rewardItem.deductedPoint || ''}
                    onChange={(e) =>
                        handleInputUpdate('deductedPoint', e.target.value)
                    }
                    onWheel={(e) => e.target.blur()}
                    disabled={isDisabled}
                />
            )}
            {(rewardItem.promotionType === 'PICK_A_PRIZE' ||
                rewardItem.promotionType === 'REWARD') && (
                <TextField
                    fullWidth
                    label="Win Rate (%)"
                    type="number"
                    value={rewardItem.winRate || ''}
                    onChange={(e) =>
                        handleInputUpdate('winRate', e.target.value)
                    }
                    inputProps={{ min: 0.01, max: 100, step: 0.01 }}
                    onWheel={(e) => e.target.blur()}
                    disabled={isDisabled}
                    helperText="Enter a value between 0.01 and 100"
                />
            )}
            {(rewardItem.promotionType === 'LUCKY_PROMOTION' ||
                rewardItem.promotionType === 'REWARD' ||
                rewardItem.promotionType === 'PICK_A_PRIZE') && (
                <TextField
                    fullWidth
                    label="Win Limit"
                    type="number"
                    value={rewardItem.winLimit || ''}
                    onChange={(e) =>
                        handleInputUpdate('winLimit', e.target.value)
                    }
                    onWheel={(e) => e.target.blur()}
                    disabled={isDisabled}
                />
            )}
            {(rewardItem.promotionType === 'MGM' ||
                rewardItem.promotionType === 'LUCKY_PROMOTION' ||
                rewardItem.promotionType === 'REWARD' ||
                rewardItem.promotionType === 'PICK_A_PRIZE') && (
                <TextField
                    fullWidth
                    label="Win Limit Per User"
                    type="number"
                    value={rewardItem.winLimitPerUser || ''}
                    onChange={(e) =>
                        handleInputUpdate('winLimitPerUser', e.target.value)
                    }
                    onWheel={(e) => e.target.blur()}
                    disabled={isDisabled}
                />
            )}

            {(rewardItem.promotionType === 'LUCKY_PROMOTION' ||
                rewardItem.promotionType === 'MGM_MILESTONE') && (
                <TextField
                    fullWidth
                    label={`${
                        rewardItem.promotionType === 'MGM_MILESTONE'
                            ? 'Milestone'
                            : 'Interval'
                    }`}
                    type="number"
                    value={rewardItem.interval || ''}
                    onChange={(e) =>
                        handleInputUpdate('interval', e.target.value)
                    }
                    onWheel={(e) => e.target.blur()}
                    disabled={isDisabled}
                />
            )}
            {(rewardItem.promotionType === 'LUCKY_PROMOTION' ||
                rewardItem.promotionType === 'MGM' ||
                rewardItem.promotionType === 'PICK_A_PRIZE') && (
                <Box display="flex" gap={2} alignItems="center">
                    <DatePicker
                        label="Start Date"
                        value={
                            rewardItem.startDate
                                ? new Date(rewardItem.startDate)
                                : null
                        }
                        onChange={(value) =>
                            handleInputUpdate('startDate', value)
                        }
                        sx={{ flex: 1 }}
                        disabled={isDisabled}
                    />
                    <DatePicker
                        label="End Date"
                        value={
                            rewardItem.endDate
                                ? new Date(rewardItem.endDate)
                                : null
                        }
                        onChange={(value) =>
                            handleInputUpdate('endDate', value)
                        }
                        sx={{ flex: 1 }}
                        disabled={isDisabled}
                    />
                </Box>
            )}
            <FormControl fullWidth>
                <InputLabel
                    sx={(theme) => ({
                        backgroundColor: theme.palette.background.main,
                    })}
                >
                    Prize
                </InputLabel>
                <Select
                    value={rewardItem.loyaltyPrizeItems || []}
                    multiple={rewardItem.promotionType === 'MGM_MILESTONE'}
                    onChange={(e) =>
                        handleInputUpdate(
                            'loyaltyPrizeItems',
                            rewardItem.promotionType === 'MGM_MILESTONE'
                                ? e.target.value
                                : [e.target.value]
                        )
                    }
                    disabled={isDisabled}
                    renderValue={(selected) =>
                        Array.isArray(selected)
                            ? prizeItems
                                  .filter((prize) =>
                                      selected.includes(
                                          prize.loyaltyPrizeItemId
                                      )
                                  )
                                  .map((pr) =>
                                      pr.prizeType === 'VOUCHER'
                                          ? `${pr.prizeType} - ${pr.merchantName} -  ${pr.denominationValue} - ${pr.denominationQuantity}`
                                          : `${pr.prizeType} - ${pr.merchantName || pr.denominationValue}`
                                  )
                                  .join(', ')
                            : ''
                    }
                >
                    {prizeItems
                        .filter((prize) => prize.status === 'ACTIVE')
                        .map((pr) => (
                            <MenuItem
                                value={pr.loyaltyPrizeItemId}
                                key={pr.loyaltyPrizeItemId}
                            >
                                {pr.prizeType === 'VOUCHER'
                                    ? `${pr.prizeType} - ${pr.merchantName} -  ${pr.denominationValue} - ${pr.denominationQuantity}`
                                    : `${pr.prizeType} - ${pr.merchantName || pr.denominationValue}`}
                            </MenuItem>
                        ))}
                </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2 }}>
                <Button variant="outlined" onClick={() => handleSave('CANCEL')}>
                    Cancel
                </Button>
                {rewardItem.status === 'DRAFT' && !hasReadOnly && (
                    <Button
                        variant="outlined"
                        onClick={() => handleSave('DELETE')}
                    >
                        DELETE
                    </Button>
                )}
                {(!rewardItem.status || rewardItem.status == 'DRAFT') && !hasReadOnly &&(
                    <>
                        <Button
                            variant="outlined"
                            onClick={() => handleSave('DRAFT')}
                        >
                            Save as Draft
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handleSave('ACTIVE')}
                        >
                            Publish
                        </Button>
                    </>
                )}
            </Box>
        </Box>
    );
};

export default Reward;
